import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, FileText, Trash2, Check, Loader2, Mail, BarChart3, Search, Image as ImageIcon, ExternalLink } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin AI Studio — Director's automation cockpit.
 * Four tools: Blog Drafter · SEO Autowriter · Admin Insights · Director Digest.
 */
export default function AdminAIStudio() {
    const [tab, setTab] = useState("blog");
    const tabs = [
        { id: "blog", label: "Blog Drafter", icon: FileText },
        { id: "seo", label: "SEO Autowriter", icon: Search },
        { id: "insights", label: "Admin Insights", icon: BarChart3 },
        { id: "digest", label: "Weekly Digest", icon: Mail },
    ];
    return (
        <div className="rounded-sm border border-forest/15 bg-cream-warm overflow-hidden" data-testid="admin-ai-studio">
            <div className="px-5 py-4 border-b border-forest/15 flex items-center gap-2 bg-cream">
                <Sparkles size={16} className="text-gold" />
                <h2 className="font-serif text-xl text-forest">AI Studio</h2>
                <span className="ml-2 text-[0.6rem] tracking-[0.3em] uppercase text-brown/60">Automation Doctrine</span>
            </div>
            <div className="flex flex-wrap border-b border-forest/15 bg-cream">
                {tabs.map((t) => {
                    const I = t.icon;
                    return (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            className={`px-4 py-3 text-[0.7rem] tracking-[0.22em] uppercase inline-flex items-center gap-2 border-b-2 ${
                                tab === t.id ? "border-gold text-forest" : "border-transparent text-brown/70 hover:text-forest"
                            }`}
                            data-testid={`ai-tab-${t.id}`}
                        >
                            <I size={13} /> {t.label}
                        </button>
                    );
                })}
            </div>
            <div className="p-5">
                {tab === "blog" && <BlogDrafterPane />}
                {tab === "seo" && <SeoAutowriterPane />}
                {tab === "insights" && <AdminInsightsPane />}
                {tab === "digest" && <DigestPane />}
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
function BlogDrafterPane() {
    const [drafts, setDrafts] = useState([]);
    const [topic, setTopic] = useState("");
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);
    const [active, setActive] = useState(null);

    const load = async () => {
        try {
            const { data } = await api.get("/ai/blog/drafts");
            setDrafts(data.drafts || []);
        } catch { /* ignore */ }
    };
    useEffect(() => { load(); }, []);

    const generate = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/ai/blog/draft", {
                topic: topic || undefined,
                seed_notes: notes || undefined,
                target_word_count: 650,
            });
            setActive(data);
            toast.success(`Drafted: ${data.title}`);
            setTopic(""); setNotes("");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Draft failed");
        } finally { setBusy(false); }
    };

    const approve = async (draft_id) => {
        try {
            toast.message("Publishing & generating OG image…");
            const { data } = await api.post("/ai/blog/approve", { draft_id });
            toast.success("Published");
            setActive(data);
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    const regenOg = async (draft_id) => {
        try {
            toast.message("Regenerating OG image…");
            await api.post(`/ai/blog/regenerate-og/${draft_id}`);
            toast.success("OG image refreshed");
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    const remove = async (draft_id) => {
        if (!window.confirm("Delete this draft?")) return;
        try {
            await api.delete(`/ai/blog/drafts/${draft_id}`);
            toast.success("Deleted");
            if (active?.id === draft_id) setActive(null);
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    return (
        <div className="grid lg:grid-cols-2 gap-5" data-testid="ai-blog-pane">
            <div className="space-y-3">
                <label className="text-[0.65rem] tracking-[0.3em] uppercase text-brown">Topic (optional)</label>
                <input
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    placeholder="Leave blank to auto-pick from rotation"
                    className="w-full px-3 py-2.5 rounded-sm bg-cream border border-forest/20 text-forest text-sm"
                    data-testid="ai-blog-topic"
                />
                <label className="text-[0.65rem] tracking-[0.3em] uppercase text-brown">Seed notes (optional)</label>
                <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Any thoughts Ymaku wants worked in..."
                    rows={5}
                    className="w-full px-3 py-2.5 rounded-sm bg-cream border border-forest/20 text-forest text-sm"
                    data-testid="ai-blog-notes"
                />
                <button
                    onClick={generate}
                    disabled={busy}
                    className="btn-pill btn-gold disabled:opacity-60"
                    data-testid="ai-blog-generate"
                >
                    {busy ? <><Loader2 size={14} className="animate-spin" /> Drafting…</> : <><Sparkles size={14} /> Draft New Post</>}
                </button>

                <div className="pt-4 mt-4 border-t border-forest/10">
                    <h4 className="text-[0.65rem] tracking-[0.3em] uppercase text-brown mb-2">Recent Drafts</h4>
                    <div className="space-y-1 max-h-80 overflow-y-auto">
                        {drafts.map((d) => (
                            <button
                                key={d.id}
                                onClick={() => setActive(d)}
                                className={`w-full text-left px-3 py-2 rounded-sm text-sm hover:bg-forest/5 ${active?.id === d.id ? "bg-forest/5" : ""}`}
                                data-testid={`ai-blog-draft-row-${d.id}`}
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <span className="truncate text-forest">{d.title}</span>
                                    <span className={`text-[0.55rem] tracking-[0.2em] uppercase shrink-0 ${d.status === "published" ? "text-gold" : "text-brown/60"}`}>
                                        {d.status}
                                    </span>
                                </div>
                            </button>
                        ))}
                        {drafts.length === 0 && <p className="text-xs text-brown/50">No drafts yet.</p>}
                    </div>
                </div>
            </div>

            <div className="rounded-sm border border-forest/15 p-5 bg-cream overflow-y-auto max-h-[600px]" data-testid="ai-blog-preview">
                {active ? (
                    <>
                        <div className="flex items-start justify-between gap-3">
                            <h3 className="font-serif text-2xl text-forest">{active.title}</h3>
                            <div className="shrink-0 flex gap-2 flex-wrap">
                                {active.status !== "published" && (
                                    <button onClick={() => approve(active.id)} className="btn-pill btn-gold text-xs" data-testid="ai-blog-approve">
                                        <Check size={12} /> Approve
                                    </button>
                                )}
                                {active.status === "published" && (
                                    <>
                                        <a
                                            href={`${BACKEND.replace(/^https?:\/\//, "https://")}/blog/${active.slug}`.replace(BACKEND, "")}
                                            onClick={(e) => { e.preventDefault(); window.open(`/blog/${active.slug}`, "_blank"); }}
                                            className="btn-pill btn-ghost text-xs"
                                            data-testid="ai-blog-view-live"
                                        >
                                            <ExternalLink size={12} /> View Live
                                        </a>
                                        <button onClick={() => regenOg(active.id)} className="btn-pill btn-ghost text-xs" data-testid="ai-blog-regen-og" title="Regenerate OG image">
                                            <ImageIcon size={12} /> Regen OG
                                        </button>
                                    </>
                                )}
                                <button onClick={() => remove(active.id)} className="btn-pill btn-ghost text-xs" data-testid="ai-blog-delete">
                                    <Trash2 size={12} />
                                </button>
                            </div>
                        </div>
                        <p className="text-xs text-brown/60 mt-1">/{active.slug}</p>
                        {active.og_image_generated_at && (
                            <div className="mt-3 aspect-[16/9] overflow-hidden rounded-sm border border-forest/15 bg-black/5">
                                <img src={`${BACKEND}/api/blog/og/${active.id}.png?v=${active.og_image_generated_at}`} alt="OG preview" className="w-full h-full object-cover" />
                            </div>
                        )}
                        <p className="mt-3 text-sm italic text-brown">{active.excerpt}</p>
                        <div className="mt-5 text-sm text-forest/90 whitespace-pre-wrap leading-relaxed">{active.body_markdown}</div>
                        {active.seo && (
                            <div className="mt-6 pt-4 border-t border-forest/10 text-xs text-brown/70 space-y-1">
                                <p><strong>SEO Title:</strong> {active.seo.title}</p>
                                <p><strong>SEO Description:</strong> {active.seo.description}</p>
                                <p><strong>OG Image:</strong> {active.seo.og_image_hint}</p>
                            </div>
                        )}
                        {active.suggested_tags?.length > 0 && (
                            <div className="mt-3 flex flex-wrap gap-1.5">
                                {active.suggested_tags.map((t) => (
                                    <span key={t} className="text-[0.6rem] tracking-[0.15em] uppercase px-2 py-0.5 rounded-full bg-gold/10 text-brown border border-gold/30">{t}</span>
                                ))}
                            </div>
                        )}
                    </>
                ) : (
                    <p className="text-sm text-brown/50">Pick a draft, or draft a new post.</p>
                )}
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
function SeoAutowriterPane() {
    const [url, setUrl] = useState("/inventory");
    const [title, setTitle] = useState("");
    const [summary, setSummary] = useState("");
    const [out, setOut] = useState(null);
    const [busy, setBusy] = useState(false);
    const run = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/ai/seo-meta", {
                page_url: url, page_title: title, page_summary: summary,
            });
            setOut(data);
            toast.success("SEO meta generated");
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };
    return (
        <div className="grid lg:grid-cols-2 gap-5" data-testid="ai-seo-pane">
            <div className="space-y-3">
                <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="/inventory" className="w-full px-3 py-2.5 rounded-sm bg-cream border border-forest/20 text-sm" data-testid="ai-seo-url" />
                <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Page title" className="w-full px-3 py-2.5 rounded-sm bg-cream border border-forest/20 text-sm" data-testid="ai-seo-title" />
                <textarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={4} placeholder="Short summary of the page content" className="w-full px-3 py-2.5 rounded-sm bg-cream border border-forest/20 text-sm" data-testid="ai-seo-summary" />
                <button onClick={run} disabled={busy || !title || !summary} className="btn-pill btn-gold disabled:opacity-60" data-testid="ai-seo-run">
                    {busy ? <><Loader2 size={14} className="animate-spin" /> Writing…</> : <><Sparkles size={14} /> Generate Meta</>}
                </button>
            </div>
            <div className="rounded-sm border border-forest/15 p-5 bg-cream text-sm" data-testid="ai-seo-out">
                {out ? (
                    <div className="space-y-2 text-forest/90">
                        <p><strong>Title:</strong> {out.meta_title}</p>
                        <p><strong>Description:</strong> {out.meta_description}</p>
                        <p><strong>OG Title:</strong> {out.og_title}</p>
                        <p><strong>OG Description:</strong> {out.og_description}</p>
                        <p><strong>OG Image Prompt:</strong> {out.og_image_prompt}</p>
                        <p><strong>Keywords:</strong> {(out.focus_keywords || []).join(", ")}</p>
                    </div>
                ) : <p className="text-brown/50">Output will appear here.</p>}
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
function AdminInsightsPane() {
    const [question, setQuestion] = useState("");
    const [messages, setMessages] = useState([]);
    const [session, setSession] = useState(null);
    const [busy, setBusy] = useState(false);

    const ask = async () => {
        if (!question.trim()) return;
        const q = question;
        setQuestion("");
        setMessages((m) => [...m, { role: "user", text: q }]);
        setBusy(true);
        try {
            const { data } = await api.post("/ai/admin-insights", { question: q, session_id: session || undefined });
            setSession(data.session_id);
            setMessages((m) => [...m, { role: "assistant", text: data.answer }]);
        } catch (e) {
            setMessages((m) => [...m, { role: "assistant", text: `Error: ${e?.response?.data?.detail || "Failed"}` }]);
        } finally { setBusy(false); }
    };

    return (
        <div className="space-y-3" data-testid="ai-insights-pane">
            <div className="rounded-sm border border-forest/15 p-4 bg-cream max-h-96 overflow-y-auto space-y-3">
                {messages.length === 0 && (
                    <p className="text-sm text-brown/50">
                        Ask Island Gold's war-room anything — "How many waitlist this week?", "What's the reserve balance?", "What drafts are pending?"
                    </p>
                )}
                {messages.map((m, i) => (
                    <div key={i} className={`text-sm ${m.role === "user" ? "text-forest" : "text-brown"}`}>
                        <span className="text-[0.6rem] tracking-[0.3em] uppercase text-brown/50 block">{m.role === "user" ? "Director" : "War Room"}</span>
                        <p className="whitespace-pre-wrap">{m.text}</p>
                    </div>
                ))}
                {busy && <div className="text-xs text-brown/60 flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Thinking…</div>}
            </div>
            <div className="flex gap-2">
                <input
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && !busy && ask()}
                    placeholder="Ask a question…"
                    className="flex-1 px-3 py-2.5 rounded-sm bg-cream border border-forest/20 text-sm"
                    data-testid="ai-insights-input"
                />
                <button onClick={ask} disabled={busy || !question.trim()} className="btn-pill btn-gold disabled:opacity-60" data-testid="ai-insights-send">Ask</button>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
function DigestPane() {
    const [digest, setDigest] = useState(null);
    const [busy, setBusy] = useState(false);
    const preview = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/ai/digest/preview", {});
            setDigest(data);
            toast.success("Digest generated");
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };
    return (
        <div className="space-y-4" data-testid="ai-digest-pane">
            <div className="flex items-center justify-between">
                <p className="text-sm text-brown">Preview this week's Monday-morning Director's Digest.</p>
                <button onClick={preview} disabled={busy} className="btn-pill btn-gold disabled:opacity-60" data-testid="ai-digest-preview">
                    {busy ? <><Loader2 size={14} className="animate-spin" /> Composing…</> : <><Mail size={14} /> Preview</>}
                </button>
            </div>
            {digest && (
                <div className="rounded-sm border border-forest/15 p-5 bg-cream" data-testid="ai-digest-out">
                    <h3 className="font-serif text-xl text-forest">{digest.subject}</h3>
                    <div className="mt-3 text-sm text-forest/90 whitespace-pre-wrap">{digest.body_markdown}</div>
                </div>
            )}
        </div>
    );
}
